import { AccessTime as AccessTimeIcon, Add as AddIcon, Check } from '@mui/icons-material';
import { Stack, ToggleButton, Tooltip } from "@mui/material";
import { Dayjs } from 'dayjs';
import { useEffect, useState } from "react";
import { useTodos } from '../../context/TodoContext/TodoContext';
import CalendarGrid from "./components/CalendarGrid";
import { CalendarHeader } from "./components/CalendarHeader";
import { useCalendar } from './hooks/useCalendar';
import TodoDetail from '../TodoDetail/TodoDetail';
import { useNavigate, useParams } from 'react-router-dom';

const FullCalendar = () => {
    const { setActiveWorkspace } = useTodos();
    const [isAddMode, setIsAddMode] = useState(false);
    const [showTime, setShowTime] = useState(false);

    const {
        currentDate,
        selectedDate,
        selectedTodoId,
        handlePrevMonth,
        handleNextMonth,
        getTodosForDate,
        getTodoColor,
        handleTodoClick,
        handleCloseDetail,
        generateCalendarDays,
    } = useCalendar();

    const { activeWorkspace, addTodoByTodoData } = useTodos();

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const calendarDays = generateCalendarDays();

    const navigate = useNavigate();
    const { workspaceId: paramWorkspaceId } = useParams();
    const [workspaceId, setWorkspaceId] = useState<string | null>(null);

    useEffect(() => {
        if (paramWorkspaceId) {
            setActiveWorkspace(paramWorkspaceId);
            setWorkspaceId(paramWorkspaceId);
        }
        else
            navigate(`/calendar/${activeWorkspace}`, { replace: true });
    }, [workspaceId, navigate, activeWorkspace, paramWorkspaceId, setActiveWorkspace]);

    const handleSaveTodo = (date: Dayjs, text: string) => {
        if (!text.trim()) return;
        const todo = addTodoByTodoData({
            text,
            dueDate: date,
            priority: 'medium',
            completed: false,
        });
        setIsAddMode(false);
        return todo;
    };

    return (
        <Stack direction="row" sx={{ height: '100%', width: '100%', position: 'relative' }}>
            <Stack sx={{
                height: '100%',
                flex: 1,
                minWidth: { xs: '300px', sm: '600px' },
                overflow: 'auto',
                transition: 'width 0.3s ease',
                width: selectedTodoId ? '60%' : '100%',
                position: 'relative',
            }}>
                <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 2 }} flexWrap='wrap' gap={1} >
                    <CalendarHeader
                        currentDate={currentDate}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="flex-end" flexGrow={1}>
                        <Tooltip title="Show Time" >
                            <ToggleButton
                                value="showTime"
                                selected={showTime}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setShowTime(!showTime);
                                }}
                                sx={{
                                    borderRadius: 20,
                                    px: 1.5,
                                    py: 0.5,
                                    mr: 2,
                                    border: '1px solid',
                                    borderColor: 'secondary.main',
                                    color: "secondary.main",
                                    '&:hover': {
                                        bgcolor: 'secondary.dark',
                                        color: 'secondary.contrastText',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 400,
                                    boxShadow: 'none',
                                    '&.Mui-selected': {
                                        bgcolor: 'secondary.main',
                                        color: 'secondary.contrastText',
                                        '&:hover': {
                                            bgcolor: 'secondary.dark',
                                            color: 'secondary.contrastText',
                                        },
                                    },
                                }}
                            >
                                <AccessTimeIcon
                                    sx={{
                                        fontSize: '1rem',
                                    }}
                                />
                                Time
                                {showTime && <Check sx={{ ml: 0.5, fontSize: '0.875rem' }} />}
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title={isAddMode ? "Cancel" : "Add Todo"}>
                            <ToggleButton
                                value="add"
                                selected={isAddMode}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setIsAddMode(!isAddMode);
                                }}
                                sx={{
                                    borderRadius: 20,
                                    px: 2,
                                    py: 0.75,
                                    mr: 2,
                                    bgcolor: isAddMode ? 'error.main' : 'primary.main',
                                    color: isAddMode ? 'error.contrastText' : 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: isAddMode ? 'error.dark' : 'primary.dark',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    textTransform: 'uppercase',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    boxShadow: 1,
                                    '&.Mui-selected': {
                                        bgcolor: isAddMode ? 'error.main' : 'primary.main',
                                        color: isAddMode ? 'error.contrastText' : 'primary.contrastText',
                                        '&:hover': {
                                            bgcolor: isAddMode ? 'error.dark' : 'primary.dark',
                                        },
                                    },
                                }}
                            >
                                <AddIcon
                                    sx={{
                                        transform: isAddMode ? 'rotate(45deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease-in-out',
                                        fontSize: '1.125rem',
                                    }}
                                />
                                {isAddMode ? 'Cancel' : 'Add Todo'}
                            </ToggleButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <CalendarGrid
                    weekDays={weekDays}
                    calendarDays={calendarDays}
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    getTodosForDate={getTodosForDate}
                    getTodoColor={getTodoColor}
                    handleTodoClick={handleTodoClick}
                    isAddMode={isAddMode}
                    setAddMode={setIsAddMode}
                    onSaveTodo={handleSaveTodo}
                    onDateClick={() => setIsAddMode(true)}
                    showTime={showTime}
                />
            </Stack>

            {selectedTodoId && (
                <TodoDetail
                    id={selectedTodoId}
                    onClose={handleCloseDetail}
                    sticky
                />
            )}
        </Stack>
    );
};

export default FullCalendar;